import { useRef, useState, useEffect } from 'react';
import EsriMap from '@arcgis/core/Map';
import Basemap from '@arcgis/core/Basemap';
import MapView from '@arcgis/core/views/MapView';
import esriConfig from '@arcgis/core/config';
import { when } from '@arcgis/core/core/reactiveUtils';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import { EsriBasemapId } from './types';
import type { EsriMapConfig } from './types';
import { mapBackgroundTypeToEsriBasemap } from './utils/basemapMapping';

const WORLD_EXTENT = new Extent({
	xmin: -180,
	ymin: -90,
	xmax: 180,
	ymax: 90,
	spatialReference: { wkid: 4326 },
});

function centerInWorldExtent(center: Point): boolean {
	const geo =
		center.spatialReference?.isWebMercator ||
		center.spatialReference?.wkid === 3857
			? (webMercatorUtils.webMercatorToGeographic(center) as Point)
			: center;
	//odd bug in the map exent where you can grab the map and move it to beyond the North or South poles, s
	// so we need to restrict the map to the poles manually
	return WORLD_EXTENT.contains(geo) && geo.y >= -60 && geo.y <= 75;
}

export const useEsriMap = (config: EsriMapConfig) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const viewRef = useRef<MapView | null>(null);
	const esriMapRef = useRef<EsriMap | null>(null);
	const previousValidCenterRef = useRef<Point | null>(null);

	const [view, setView] = useState<MapView | null>(null);
	const [map, setMap] = useState<EsriMap | null>(null);
	const [isReady, setIsReady] = useState(false);
	const [mapCenter, setMapCenter] = useState<Point | null>(null);

	useEffect(() => {
		if (!mapRef.current) {
			console.warn(
				'[inno-map] useEsriMap: map container ref is null — MapView will not be created. Ensure the map div is mounted before this effect runs.',
			);
			return;
		}

		if (config.apiKey) {
			esriConfig.apiKey = config.apiKey;
		}

		const basemapId = mapBackgroundTypeToEsriBasemap(
			config.background ?? config.basemap ?? EsriBasemapId.OpenStreets,
		);
		const esriMap = new EsriMap({
			basemap: new Basemap({ style: { id: basemapId } }),
		});

		const mapView = new MapView({
			container: mapRef.current,
			map: esriMap,
			center: config.center ?? [0, 0],
			zoom: config.zoom ?? 3,
			constraints: {
				minZoom: config.minZoom ?? 2,
				maxZoom: config.maxZoom ?? 23,
				rotationEnabled: false,
			},
		});
		// remove Esri's zoom control
		mapView.ui.remove('zoom');

		mapView.when(() => {
			viewRef.current = mapView;
			esriMapRef.current = esriMap;
			setMap(esriMap);
			setView(mapView);
			setIsReady(true);

			// Test hook: expose the live MapView/Map to Cypress so E2E specs can
			// read camera state and drive the map. No-op outside Cypress.
			if (typeof window !== 'undefined' && window.Cypress) {
				window.esriMapView = mapView;
				window.esriMap = esriMap;
			}
		});

		const handle = when(
			() => mapView.stationary,
			() => {
				setMapCenter(mapView.center);
			},
		);

		return () => {
			handle.remove();
			viewRef.current = null;
			esriMapRef.current = null;
			mapView.destroy();

			if (typeof window !== 'undefined' && window.Cypress) {
				delete window.esriMapView;
				delete window.esriMap;
			}
		};
	}, []);

	useEffect(() => {
		if (!mapCenter || !viewRef.current) return;
		const mapView = viewRef.current;

		if (!centerInWorldExtent(mapCenter)) {
			const prev = previousValidCenterRef.current;
			if (prev) {
				void mapView.goTo({
					center: prev,
					zoom: mapView.zoom,
				});
			} else {
				// if no previous valid center, center the map on the equator
				void mapView.goTo({
					center: [0, 0],
					zoom: 4,
				});
			}
			return;
		}

		previousValidCenterRef.current = mapCenter.clone();
	}, [mapCenter]);

	// Reactively navigate when center/zoom props change after initial mount.
	// viewRef avoids re-triggering on first render (view is null until .when() resolves).
	// Use primitive values as deps — callers often pass inline arrays which create a new
	// reference on every render, which would override fit-to-extents on every re-render.
	const centerLng = config.center?.[0];
	const centerLat = config.center?.[1];
	useEffect(() => {
		if (!viewRef.current) return;
		viewRef.current.goTo({
			center: config.center,
			zoom: config.zoom,
		});
	}, [centerLng, centerLat, config.zoom]);

	// Note: Basemap changes are now handled by the useBasemap hook in ReactEsriMap
	// via the EsriMapBasemap component. Initial basemap is set in the Map constructor above (line 43).

	return { mapRef, view, map, isReady };
};
